import { z } from 'zod';

/**
 * `mcp.json` import: the `mcpServers` map used by Claude Desktop, Claude Code (`.mcp.json`), Cursor
 * and Windsurf, VS Code's `servers` map (`.vscode/mcp.json`) and its `settings.json` (`mcp.servers`).
 *
 *   { "mcpServers": { "github": { "command": "npx", "args": ["-y", "…"], "env": { "TOKEN": "…" } },
 *                     "docs":   { "url": "https://…/mcp", "headers": { "Authorization": "Bearer …" } } } }
 */
export const importMcpJsonRequestSchema = z.object({
  content: z.string().min(1, 'Paste your mcp.json').max(200_000),
  /** Only report what would happen. */
  dryRun: z.boolean().default(false),
});
export type ImportMcpJsonRequest = z.infer<typeof importMcpJsonRequestSchema>;

/** A value found in the file: a literal, or a reference to a variable (`${VAR}`, `${env:VAR}`, `${input:id}`). */
export type McpJsonValue = { literal: string } | { variable: string } | { template: string };

export interface ParsedMcpServer {
  /** The key in the file. */
  key: string;
  /** A valid platform name derived from the key. */
  name: string;
  transport: 'stdio' | 'streamable_http';
  command: string | null;
  args: string[];
  url: string | null;
  env: Record<string, McpJsonValue>;
  headers: Record<string, McpJsonValue>;
  warnings: string[];
}

export interface ParsedMcpJson {
  servers: ParsedMcpServer[];
  /** Problems with the file as a whole or with servers that were left out. */
  errors: Array<{ key: string | null; message: string }>;
}

const VARIABLE = /\$\{(?:(env|input):)?([A-Za-z_][A-Za-z0-9_.-]*)\}/g;
const WHOLE_VARIABLE = /^\$\{(?:(env|input):)?([A-Za-z_][A-Za-z0-9_.-]*)\}$/;

/** A placeholder name as an environment variable key: `github.token` → `GITHUB_TOKEN`. */
export const toEnvKey = (name: string) =>
  name
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^(\d)/, '_$1')
    .toUpperCase()
    .slice(0, 100);

/** A server key as a platform name: `GitHub MCP` → `github-mcp`. */
export function toServerName(key: string): string {
  const name = key
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/g, '')
    .slice(0, 64);
  return name || 'mcp-server';
}

function classify(value: string): McpJsonValue {
  const whole = WHOLE_VARIABLE.exec(value.trim());
  if (whole) return { variable: toEnvKey(whole[2]!) };
  if (VARIABLE.test(value)) {
    VARIABLE.lastIndex = 0;
    return {
      template: value.replace(
        VARIABLE,
        (_m, _ns: string, name: string) => `{{env.${toEnvKey(name)}}}`,
      ),
    };
  }
  return { literal: value };
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Finds the server map in any of the supported layouts. */
function serverMap(doc: Record<string, unknown>): Record<string, unknown> | null {
  if (isRecord(doc.mcpServers)) return doc.mcpServers;
  if (isRecord(doc.servers)) return doc.servers;
  if (isRecord(doc.mcp) && isRecord(doc.mcp.servers)) return doc.mcp.servers;
  // A bare map of servers: every value looks like a server.
  const values = Object.values(doc);
  if (
    values.length > 0 &&
    values.every((v) => isRecord(v) && ('command' in v || 'url' in v || 'serverUrl' in v))
  )
    return doc;
  return null;
}

/** Parses mcp.json text. Never throws: problems are reported in `errors` and `warnings`. */
export function parseMcpJson(text: string): ParsedMcpJson {
  let doc: unknown;
  try {
    // Allow the comments and trailing commas VS Code writes (JSONC).
    doc = JSON.parse(
      text
        .replace(/("(?:[^"\\]|\\.)*")|\/\*[\s\S]*?\*\/|\/\/[^\n]*/g, (m, str: string) => str ?? '')
        .replace(/,\s*([}\]])/g, '$1'),
    );
  } catch {
    return { servers: [], errors: [{ key: null, message: 'This is not valid JSON.' }] };
  }
  if (!isRecord(doc))
    return { servers: [], errors: [{ key: null, message: 'Expected a JSON object.' }] };
  const map = serverMap(doc);
  if (!map)
    return {
      servers: [],
      errors: [
        {
          key: null,
          message: 'No servers found. Expected "mcpServers": { … } or "servers": { … }.',
        },
      ],
    };

  const servers: ParsedMcpServer[] = [];
  const errors: ParsedMcpJson['errors'] = [];
  const names = new Set<string>();
  for (const [key, raw] of Object.entries(map)) {
    if (!isRecord(raw)) {
      errors.push({ key, message: 'Not a server object' });
      continue;
    }
    if (raw.disabled === true) {
      errors.push({ key, message: 'Disabled in the file (skipped)' });
      continue;
    }
    const warnings: string[] = [];
    const type = typeof raw.type === 'string' ? raw.type.toLowerCase() : null;
    const url =
      typeof raw.url === 'string'
        ? raw.url
        : typeof raw.serverUrl === 'string'
          ? raw.serverUrl
          : null;
    const command = typeof raw.command === 'string' ? raw.command.trim() : null;

    let transport: ParsedMcpServer['transport'];
    if (url && type !== 'stdio') {
      transport = 'streamable_http';
      if (type === 'sse')
        warnings.push('SSE servers are connected with Streamable HTTP; most servers support both');
    } else if (command) {
      transport = 'stdio';
    } else {
      errors.push({ key, message: 'Needs a "command" (stdio) or a "url" (HTTP)' });
      continue;
    }

    const args = Array.isArray(raw.args) ? raw.args.map((a) => String(a)) : [];
    if (args.some((a) => /\$\{[^}]+\}/.test(a)))
      warnings.push('Arguments with ${…} are kept as written (they are not substituted)');
    if (typeof raw.cwd === 'string')
      warnings.push('"cwd" is ignored; servers run in a scratch directory');
    if (raw.envFile !== undefined)
      warnings.push('"envFile" is ignored; add its values under "env"');

    const env: Record<string, McpJsonValue> = {};
    if (isRecord(raw.env))
      for (const [k, v] of Object.entries(raw.env)) {
        const text = v === null || v === undefined ? '' : String(v);
        env[k] = text === '' ? { variable: toEnvKey(k) } : classify(text);
      }
    const headers: Record<string, McpJsonValue> = {};
    if (isRecord(raw.headers))
      for (const [k, v] of Object.entries(raw.headers)) headers[k] = classify(String(v ?? ''));
    if (transport === 'stdio' && Object.keys(headers).length > 0)
      warnings.push('"headers" are ignored for stdio servers');
    if (transport === 'streamable_http' && Object.keys(env).length > 0)
      warnings.push('"env" is ignored for HTTP servers');

    let name = toServerName(key);
    for (let i = 2; names.has(name); i++) name = `${toServerName(key).slice(0, 60)}-${i}`;
    names.add(name);
    servers.push({
      key,
      name,
      transport,
      command: transport === 'stdio' ? command : null,
      args: transport === 'stdio' ? args : [],
      url: transport === 'streamable_http' ? url : null,
      env: transport === 'stdio' ? env : {},
      headers: transport === 'streamable_http' ? headers : {},
      warnings,
    });
  }
  return { servers, errors };
}

// --- Import result ---

export const mcpImportItemSchema = z.object({
  key: z.string(),
  name: z.string(),
  transport: z.enum(['streamable_http', 'stdio']),
  /** The resolved absolute command (stdio) or URL. */
  target: z.string().nullable(),
  args: z.array(z.string()),
  status: z.enum(['create', 'created', 'skip']),
  reason: z.string().nullable(),
  warnings: z.array(z.string()),
  /** Environment variables the server reads, and whether the import stores them. */
  variables: z.array(
    z.object({
      key: z.string(),
      /** Where it is used: `env.GITHUB_TOKEN` or `header.Authorization`. */
      usedBy: z.string(),
      /** `store`: a value from the file is saved as a secret; `existing`: already set; `missing`: set it later. */
      action: z.enum(['store', 'existing', 'missing']),
    }),
  ),
});
export type McpImportItem = z.infer<typeof mcpImportItemSchema>;

export const mcpImportResultSchema = z.object({
  dryRun: z.boolean(),
  items: z.array(mcpImportItemSchema),
  errors: z.array(z.object({ key: z.string().nullable(), message: z.string() })),
});
export type McpImportResult = z.infer<typeof mcpImportResultSchema>;
